import CloudflareClient from './client';
import { ValidationError, NotFoundError } from '../utils/errors';

/**
 * DNS API 封装
 */
class DNSAPI extends CloudflareClient {
  constructor(config) {
    super(config);
  }

  /**
   * 列出 DNS 记录
   * @param {string} domain - 域名
   * @param {object} filters - 过滤条件 (type, name, content, etc.)
   */
  async listRecords(domain, filters = {}) {
    const zoneId = await this.getZoneId(domain);

    console.debug(`Listing DNS records for ${domain}`);

    const params = {
      per_page: 100,
      ...filters
    };

    const response = await this.get(`/zones/${zoneId}/dns_records`, params);
    return response.result || [];
  }

  /**
   * 获取单个 DNS 记录
   * @param {string} domain - 域名
   * @param {string} recordId - 记录 ID
   */
  async getRecord(domain, recordId) {
    const zoneId = await this.getZoneId(domain);

    console.debug(`Getting DNS record ${recordId} for ${domain}`);

    const response = await this.get(`/zones/${zoneId}/dns_records/${recordId}`);
    return response.result;
  }

  /**
   * 创建 DNS 记录
   * @param {string} domain - 域名
   * @param {object} record - 记录信息
   */
  async createRecord(domain, record) {
    const zoneId = await this.getZoneId(domain);

    // 验证记录
    this.validateRecord(record);

    console.debug(`Creating DNS record for ${domain}:`, record);

    const response = await this.post(`/zones/${zoneId}/dns_records`, record);
    return response.result;
  }

  /**
   * 更新 DNS 记录
   * @param {string} domain - 域名
   * @param {string} recordId - 记录 ID
   * @param {object} data - 更新数据
   */
  async updateRecord(domain, recordId, data) {
    const zoneId = await this.getZoneId(domain);

    console.debug(`Updating DNS record ${recordId} for ${domain}`);

    const response = await this.put(`/zones/${zoneId}/dns_records/${recordId}`, data);
    return response.result;
  }

  /**
   * 部分更新 DNS 记录
   * @param {string} domain - 域名
   * @param {string} recordId - 记录 ID
   * @param {object} data - 更新数据
   */
  async patchRecord(domain, recordId, data) {
    const zoneId = await this.getZoneId(domain);

    console.debug(`Patching DNS record ${recordId} for ${domain}`);

    const response = await this.patch(`/zones/${zoneId}/dns_records/${recordId}`, data);
    return response.result;
  }

  /**
   * 删除 DNS 记录
   * @param {string} domain - 域名
   * @param {string} recordId - 记录 ID
   */
  async deleteRecord(domain, recordId) {
    const zoneId = await this.getZoneId(domain);

    console.debug(`Deleting DNS record ${recordId} for ${domain}`);

    const response = await this.delete(`/zones/${zoneId}/dns_records/${recordId}`);
    return response.result;
  }

  /**
   * 根据名称查找记录
   * @param {string} domain - 域名
   * @param {string} name - 记录名称
   * @param {string} type - 记录类型（可选）
   */
  async findRecordsByName(domain, name, type = null) {
    const filters = { name };
    if (type) {
      filters.type = type;
    }

    const records = await this.listRecords(domain, filters);
    return records;
  }

  /**
   * 批量创建记录
   * @param {Array} records - 记录数组 [{domain, type, name, content, ...}, ...]
   */
  async batchCreateRecords(records) {
    const results = {
      success: [],
      failed: []
    };

    for (const record of records) {
      try {
        const { domain, ...recordData } = record;
        const result = await this.createRecord(domain, recordData);
        results.success.push({ record, result });
        console.log(`Created record: ${recordData.name} (${recordData.type}) -> ${recordData.content}`);
      } catch (error) {
        results.failed.push({ record, error: error.message });
        console.error(`Failed to create record: ${error.message}`);
      }
    }

    return results;
  }

  /**
   * 批量更新记录（根据前缀）
   * @param {string} domain - 域名
   * @param {string} prefix - 记录名称前缀
   * @param {object} updates - 更新内容
   */
  async batchUpdateByPrefix(domain, prefix, updates) {
    // 获取所有匹配的记录
    const allRecords = await this.listRecords(domain);
    const fullPrefix = prefix.endsWith('.') ? prefix + domain : `${prefix}.${domain}`;

    const matchingRecords = allRecords.filter(record => {
      return record.name === fullPrefix || record.name === prefix;
    });

    if (matchingRecords.length === 0) {
      throw new NotFoundError('DNS records', `with prefix "${prefix}"`);
    }

    const results = {
      success: [],
      failed: []
    };

    for (const record of matchingRecords) {
      try {
        const updated = await this.patchRecord(domain, record.id, updates);
        results.success.push({ record, updated });
        console.log(`Updated record: ${record.name} (${record.type})`);
      } catch (error) {
        results.failed.push({ record, error: error.message });
        console.error(`Failed to update record ${record.name}: ${error.message}`);
      }
    }

    return results;
  }

  /**
   * 验证 DNS 记录
   */
  validateRecord(record) {
    const requiredFields = ['type', 'name', 'content'];

    for (const field of requiredFields) {
      if (!record[field]) {
        throw new ValidationError(`Missing required field: ${field}`, field);
      }
    }

    // 验证记录类型
    const validTypes = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA', 'PTR'];
    if (!validTypes.includes(record.type.toUpperCase())) {
      throw new ValidationError(`Invalid DNS record type: ${record.type}`, 'type');
    }

    // 验证 IP 地址格式（对于 A 和 AAAA 记录）
    if (record.type === 'A') {
      if (!this.isValidIPv4(record.content)) {
        throw new ValidationError(`Invalid IPv4 address: ${record.content}`, 'content');
      }
    } else if (record.type === 'AAAA') {
      if (!this.isValidIPv6(record.content)) {
        throw new ValidationError(`Invalid IPv6 address: ${record.content}`, 'content');
      }
    }

    // 验证 TTL
    if (record.ttl !== undefined && record.ttl !== 1 && (record.ttl < 60 || record.ttl > 86400)) {
      throw new ValidationError('TTL must be 1 (auto) or between 60 and 86400', 'ttl');
    }
  }

  /**
   * 验证 IPv4 地址
   */
  isValidIPv4(ip) {
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipv4Regex.test(ip)) return false;

    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  }

  /**
   * 验证 IPv6 地址
   */
  isValidIPv6(ip) {
    const ipv6Regex = /^([\da-fA-F]{1,4}:){7}[\da-fA-F]{1,4}$|^::1$|^::$/;
    return ipv6Regex.test(ip);
  }

  /**
   * 导出记录为 JSON
   */
  async exportRecords(domain) {
    const records = await this.listRecords(domain);

    const exportData = records.map(record => ({
      type: record.type,
      name: record.name,
      content: record.content,
      ttl: record.ttl,
      proxied: record.proxied,
      priority: record.priority
    }));

    return exportData;
  }

  /**
   * 从 JSON 导入记录
   */
  async importRecords(domain, records) {
    return this.batchCreateRecords(
      records.map(record => ({ domain, ...record }))
    );
  }
}

export default DNSAPI;
